import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import { useInternalManagers } from './hooks/useInternalManagers';
import { IoMdArrowRoundBack } from "react-icons/io";
import { IoDocumentText, IoList, IoGrid, IoAdd, IoSave } from "react-icons/io5";
import { FaFileExcel } from "react-icons/fa";
import { useSignal } from "@preact/signals-react";
import useFetch from "../../../hooks/useFetch";
import PageForm from "../../templates/PageForm";
import NewTooltip from "../../atoms/NewTooltip";
import Dialog from "../../organisms/Dialog";
import CreateOperationModal from "./CreateOperationModal";
import OperationTableView from "./components/OperationTableView";
import OperationDetailView from "./components/OperationDetailView";
import ValidationConfigSection from "./ValidationConfigSection";
import OperationSummary from "./OperationSummary";
import ProcessingErrorsBanner from "./ProcessingErrorsBanner";
import {
    STRUCTURING_ROUTES,
    STRUCTURING_BREADCRUMBS,
    OperationStatus,
} from './structuringUtils';
import {
    Operation,
    buildFetchUrl,
    buildFetchHeaders,
    parseOperationData,
    reloadOperationData,
    handleDeleteDocument as deleteDocumentBackend,
    handleDeleteInstallment as deleteInstallmentBackend,
    saveDocuments,
    exportOperationToExcel,
} from './OperationPageViewBackend';
import { editedReceivablesStore } from './EditedReceivablesStore';
import type { Document as OperationDocument, Receivable } from './OperationPageViewBackend';

const OperationPageView: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const apiVersion = useSignal('');
    const viewMode = useSignal<'detail' | 'list'>('list');
    const showDeleteDialog = useSignal(false);
    const showSaveDialog = useSignal(false);
    const deleteAction = useSignal<'document' | 'installment' | null>(null);
    const selectedDocument = useSignal<string>('');
    const selectedInstallment = useSignal<string>('');
    const isDeleting = useSignal(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const internalManagers = useInternalManagers();
    const operation = useSignal<Operation>({
        operation_code: "", total_documents: 0, total_receivables: 0, total_amount: 0,
        processing_date: "", s3_location: "", documents: [], notes: "", status: ""
    });

    const fetchHeaders = useMemo(() => buildFetchHeaders(), []);
    const { isLoading, data, error, isTokenError } = useFetch(buildFetchUrl(id), fetchHeaders);

    useEffect(() => { editedReceivablesStore.clear(); return () => editedReceivablesStore.clear(); }, [id]);

    if (isTokenError) navigate("/denied");
    if (error) navigate("/network-error");

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
            operation.value = parseOperationData(data);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const handleReloadData = async () => {
        const newData = await reloadOperationData(id);
        if (newData) operation.value = newData;
    };

    const handleDeleteDocument = (documentId: string) => {
        deleteAction.value = 'document';
        selectedDocument.value = documentId;
        showDeleteDialog.value = true;
    };

    const handleDeleteInstallment = (documentId: string, installmentNumber: string) => {
        deleteAction.value = 'installment';
        selectedDocument.value = documentId;
        selectedInstallment.value = installmentNumber;
        showDeleteDialog.value = true;
    };

    const confirmDelete = async () => {
        isDeleting.value = true;
        try {
            if (deleteAction.value === 'document') {
                const result = await deleteDocumentBackend(operation.value.operation_code, selectedDocument.value);
                if (result.success) {
                    if (result.operationDeleted) navigate(STRUCTURING_ROUTES.list);
                    else await handleReloadData();
                }
            } else if (deleteAction.value === 'installment') {
                const result = await deleteInstallmentBackend(operation.value.operation_code, selectedDocument.value, selectedInstallment.value);
                if (result.success) await handleReloadData();
            }
        } finally {
            isDeleting.value = false;
            showDeleteDialog.value = false;
            deleteAction.value = null;
            selectedDocument.value = '';
            selectedInstallment.value = '';
        }
    };

    const handleReceivableChange = (document: OperationDocument, receivable: Receivable) => {
        editedReceivablesStore.upsert(document, receivable);
    };

    const confirmSaveChanges = async () => {
        const result = await saveDocuments({
            credit_operations_id: operation.value.credit_operations_id || '',
            ...editedReceivablesStore.buildPayload(),
        });
        if (result.success) { toast.success(result.message); editedReceivablesStore.clear(); await handleReloadData(); }
        else toast.error(result.message);
        showSaveDialog.value = false;
    };

    const isOperationClosed = operation.value.status === OperationStatus.CLOSED;

    return (
        <PageForm
            pageTitle={operation.value.operation_code}
            apiVersion={apiVersion.value}
            isLoading={isLoading}
            breadcrumb={[STRUCTURING_BREADCRUMBS.operations, STRUCTURING_BREADCRUMBS.operationDetail]}
            controlSlot={
                <div className="flex justify-end space-x-2">
                    <button onClick={() => setShowCreateModal(true)} disabled={isOperationClosed}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        <IoAdd /><span>Fechar Lote</span>
                    </button>
                    <button className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded" onClick={() => navigate(-1)}>
                        <IoMdArrowRoundBack />
                    </button>
                    <NewTooltip content="Exportar para Excel" alignment="left">
                        <button type="button" onClick={() => exportOperationToExcel(operation.value)}
                            className="text-white bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 font-medium rounded text-sm px-4 py-2 flex items-center">
                            <FaFileExcel className="mr-2" />Exportar
                        </button>
                    </NewTooltip>
                </div>
            }
        >
            <div className="container mx-auto p-4">
                {isOperationClosed && (
                    <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded dark:bg-green-900 dark:border-green-600 dark:text-green-300">
                        <strong>Operação Concluída:</strong> Esta operação foi fechada e não pode mais ser editada.
                    </div>
                )}

                <ProcessingErrorsBanner errors={operation.value.processing_errors} />

                <OperationSummary
                    totalAmount={operation.value.total_amount}
                    totalDocuments={operation.value.documents.length}
                    totalReceivables={operation.value.total_receivables}
                    notes={operation.value.notes}
                />

                <ValidationConfigSection
                    operation={operation.value}
                    onOperationChange={(op) => { operation.value = op; }}
                    internalManagers={internalManagers}
                />

                <div className="mb-6 bg-gray-50 dark:bg-slate-700 border border-blue-300 dark:border-blue-500 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                            <IoDocumentText className="mr-2" />
                            Documentos ({operation.value.documents.length})
                        </h3>
                        <div className="flex items-center space-x-2">
                            <div className="flex space-x-1 bg-gray-100 dark:bg-slate-600 rounded p-1">
                                <NewTooltip content="Visão em detalhe" alignment="left">
                                    <button type="button" onClick={() => viewMode.value = 'detail'}
                                        className={`p-2 rounded ${viewMode.value === 'detail' ? 'bg-white shadow text-gray-900 dark:bg-slate-500 dark:text-gray-100' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                        <IoGrid className="w-4 h-4" />
                                    </button>
                                </NewTooltip>
                                <NewTooltip content="Visão em lista" alignment="left">
                                    <button type="button" onClick={() => viewMode.value = 'list'}
                                        className={`p-2 rounded ${viewMode.value === 'list' ? 'bg-white shadow text-gray-900 dark:bg-slate-500 dark:text-gray-100' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                        <IoList className="w-4 h-4" />
                                    </button>
                                </NewTooltip>
                            </div>
                            <button type="button" onClick={() => { showSaveDialog.value = true; }} disabled={isOperationClosed}
                                className="bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium rounded text-sm px-4 py-2 flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed">
                                <IoSave className="w-4 h-4" /><span>Salvar</span>
                            </button>
                        </div>
                    </div>

                    {viewMode.value === 'detail' ? (
                        <OperationDetailView documents={operation.value.documents} totalReceivables={operation.value.total_receivables}
                            isDeleting={isDeleting.value} isOperationClosed={isOperationClosed}
                            onDeleteDocument={handleDeleteDocument} onDeleteInstallment={handleDeleteInstallment} onReceivableChange={handleReceivableChange} />
                    ) : (
                        <OperationTableView documents={operation.value.documents} isDeleting={isDeleting.value}
                            isOperationClosed={isOperationClosed} onDeleteInstallment={handleDeleteInstallment} onReceivableChange={handleReceivableChange} />
                    )}
                </div>
            </div>

            <Dialog id="delete-confirmation" show={showDeleteDialog.value} onClose={() => showDeleteDialog.value = false}
                header={deleteAction.value === 'document' ? 'Deletar Documento' : 'Deletar Parcela'}
                body={<div className="p-6"><p className="text-gray-700 dark:text-gray-300">
                    {deleteAction.value === 'document'
                        ? 'Tem certeza que deseja deletar este documento? Esta ação não pode ser desfeita. Se for o último documento da operação, a operação inteira será removida.'
                        : 'Tem certeza que deseja deletar esta parcela? Esta ação não pode ser desfeita e as parcelas restantes serão reorganizadas.'}
                </p></div>}
                footer={<div className="flex space-x-2">
                    <button type="button" onClick={() => showDeleteDialog.value = false} disabled={isDeleting.value}
                        className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                    <button type="button" onClick={confirmDelete} disabled={isDeleting.value}
                        className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50">{isDeleting.value ? 'Deletando...' : 'Deletar'}</button>
                </div>}
            />

            <Dialog id="save-confirmation" show={showSaveDialog.value} onClose={() => showSaveDialog.value = false}
                header="Salvar Alterações"
                body={<div className="p-6"><p className="text-gray-700 dark:text-gray-300">Deseja salvar as alterações realizadas nos recebíveis?</p></div>}
                footer={<div className="flex space-x-2">
                    <button type="button" onClick={() => showSaveDialog.value = false}
                        className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                    <button type="button" onClick={confirmSaveChanges}
                        className="px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 rounded dark:bg-blue-600 dark:hover:bg-blue-700">Confirmar</button>
                </div>}
            />

            <CreateOperationModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}
                onComplete={handleReloadData} operationId={id}
                operationSummary={{ totalAmount: operation.value.total_amount, totalDocuments: operation.value.documents.length, totalReceivables: operation.value.total_receivables }} />
        </PageForm>
    );
};

export default OperationPageView;
